import { useMemo } from 'react';

import { Button, Divider, Select, type TypeSelectOption } from '@thefarmersfront/kitchen';

import type { IRowNode } from 'ag-grid-community';

import { Table, TableBottomField, TableTopField } from '~/components/common';

import { getDeliveryAgencyOutboundUnitListColumnDefs } from '~/utils/delivery-agency/table';

import {
  DELIVERY_AGENCY_CREATED_SORT_OPTIONS,
  DELIVERY_AGENCY_PAGE_SIZE,
  type TypeDeliveryAgencySortBy,
} from '~/constants/delivery-agency';

import type { TypePagination } from '~/types/common.types';
import type { TypeDeliveryAgencyOutboundUnitListItem } from '~/types/delivery-agency.types';

import styles from './DeliveryAgencyOutboundUnitListTable.module.scss';

const DeliveryAgencyOutboundUnitListTable = ({
  data,
  pagination,
  sortBy,
  onChangePageLimit,
  onChangeSort,
  onChangePage,
  onChangeRowSelection,
  onClickGroupOutbounds,
  onClickUngroupOutbounds,
  selectedItems,
}: {
  data: TypeDeliveryAgencyOutboundUnitListItem[] | null;
  pagination: TypePagination;
  sortBy: TypeDeliveryAgencySortBy;
  selectedItems: TypeDeliveryAgencyOutboundUnitListItem[];
  onChangePageLimit: (value: TypeSelectOption<number>) => void;
  onChangeSort: (value: TypeDeliveryAgencySortBy) => void;
  onChangePage: (value: number) => void;
  onChangeRowSelection: (selectedRows: IRowNode<TypeDeliveryAgencyOutboundUnitListItem>[]) => void;
  onClickGroupOutbounds: () => void;
  onClickUngroupOutbounds: () => void;
}) => {
  const { columnDefs } = useMemo(
    () =>
      getDeliveryAgencyOutboundUnitListColumnDefs({
        currentPage: pagination.currentPage - 1,
        pageSize: pagination.pageSize,
      }),
    [pagination.currentPage, pagination.pageSize]
  );

  return (
    <div className={styles['table-container']}>
      <TableTopField
        dataTotalCount={pagination.totalCount}
        rightButton={
          <div className={styles['table-top-right-field']}>
            <Button
              size="tiny"
              variant="outlined"
              status="error"
              disabled={selectedItems.length === 0}
              onClick={onClickUngroupOutbounds}
            >
              합포장 취소
            </Button>
            <Divider className={styles['divider']} />
            <Button size="tiny" color="secondary" disabled={selectedItems.length === 0} onClick={onClickGroupOutbounds}>
              합포장 하기
            </Button>
          </div>
        }
      />
      <div className={styles['table-wrapper']}>
        <Table
          data={data}
          options={{ columnDefs }}
          rowStatusType={data === null ? 'INIT' : 'EMPTY'}
          onSelectionChange={onChangeRowSelection}
        />
      </div>
      <TableBottomField
        pageSizeOptions={Object.values(DELIVERY_AGENCY_PAGE_SIZE)}
        currentPage={pagination.currentPage - 1}
        totalCount={pagination.totalCount}
        pageSize={pagination.pageSize}
        onChangePageLimit={onChangePageLimit}
        onClickPage={(page) => onChangePage(page + 1)}
        leftField={
          <Select
            maxWidth="160px"
            size="tiny"
            dropdownPlacement="top-left"
            options={DELIVERY_AGENCY_CREATED_SORT_OPTIONS}
            value={DELIVERY_AGENCY_CREATED_SORT_OPTIONS.find((option) => option.value === sortBy)}
            onChange={(_: unknown, option: TypeSelectOption<TypeDeliveryAgencySortBy>) => onChangeSort(option.value)}
          />
        }
      />
    </div>
  );
};

export default DeliveryAgencyOutboundUnitListTable;
